"use client";

import { useState, useEffect } from "react";
import styles from "../css/register.module.css";
import Popup from "../../../components/Popup";
import FormInput from "../../../components/FormInput";
import PasswordInput from "../../../components/PasswordInput";
import Button from "../../../components/Button";
import Dropdown from "../../../components/Dropdown"; // Import the Dropdown component

const Register = ({ switchToLogin, closePopup }) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [formData, setFormData] = useState({
    companyname: "",
    company_reg_num: "",
    role: "Director",
    sapc_reg_num: "",
    address: "",
    phone: "",
    name: "",
    surname: "",
    idnumber: "",
    email: "",
    cellnum: "",
    password: "",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
    valid: false,
  });

  useEffect(() => {
    if (showErrorPopup) {
      const timer = setTimeout(() => setShowErrorPopup(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showErrorPopup]);

  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
        closePopup();
        switchToLogin();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup, closePopup, switchToLogin]);

  const handleChange = (eOrValue) => {
    // Support both native events and PasswordInput passing a raw value
    let name, value;
    if (eOrValue && eOrValue.target) {
      name = eOrValue.target.name;
      value = eOrValue.target.value;
    } else {
      // PasswordInput will pass a raw value when used below
      name = "password";
      value = eOrValue;
    }

    if (name === "password") {
      const length = value.length >= 8;
      const lowercase = /[a-z]/.test(value);
      const uppercase = /[A-Z]/.test(value);
      const number = /[0-9]/.test(value);
      const special = /[!@#$%^&*]/.test(value);
      const valid = length && lowercase && uppercase && number && special;
      setPasswordStrength({ length, lowercase, uppercase, number, special, valid });
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errorMessage) {
      setErrorMessage("");
      setShowErrorPopup(false);
    }
  };

  const checkEmailExists = async (email) => {
    try {
      setIsCheckingEmail(true);
      const response = await fetch(
        `${API_BASE_URL}/check-email?email=${encodeURIComponent(email)}`
      );
      const data = await response.json();
      if (data.exists) {
        setErrorMessage("This email is already registered.");
        setShowErrorPopup(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters long";
    if (!/[a-z]/.test(password)) return "Password must include a lowercase letter";
    if (!/[A-Z]/.test(password)) return "Password must include an uppercase letter";
    if (!/[0-9]/.test(password)) return "Password must include a number";
    if (!/[!@#$%^&*]/.test(password)) return "Password must include a special character";
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const requiredFields = [
      "companyname",
      "company_reg_num",
      "sapc_reg_num",
      "address",
      "phone",
      "name",
      "surname",
      "idnumber",
      "email",
      "cellnum",
      "password",
    ];

    for (const field of requiredFields) {
      const value = formData[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        setErrorMessage("Please fill in all required fields.");
        setShowErrorPopup(true);
        return;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage("Invalid email format.");
      setShowErrorPopup(true);
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (passwordValidation !== true) {
      setErrorMessage(passwordValidation);
      setShowErrorPopup(true);
      return;
    }

    if (await checkEmailExists(formData.email)) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Registration successful! Pending approval.");
        setShowSuccessPopup(true);
      } else {
        setErrorMessage(data.message || "Registration failed");
        setShowErrorPopup(true);
      }
    } catch {
      setErrorMessage("An error occurred. Please try again.");
      setShowErrorPopup(true);
    } finally {
      setIsLoading(false);
    }
  };
// Define options for the Dropdown component
const roleOptions = ["Director"];
  return (
    <div className={styles.registerContainer}>
      {showErrorPopup && (
        <Popup type="error" message={errorMessage} onClose={() => setShowErrorPopup(false)} />
      )}
      {showSuccessPopup && (
        <Popup type="success" message={successMessage} onClose={() => setShowSuccessPopup(false)} />
      )}

      <div className={styles.registerForm}>
        {/* Close button inside the form so it anchors to the register form corner */}
        <button
          type="button"
          className={styles.closeBtn}
          onClick={closePopup}
          aria-label="Close register form"
        >
          ×
        </button>

        <h1 className={styles.registerTitle}>Register</h1>
        <form onSubmit={handleRegister}>
          {/* Company Info Row */}
          <div className={styles.threeFields}>
            <FormInput
              label="Company Name"
              name="companyname"
              value={formData.companyname}
              onChange={handleChange}
            />
            <FormInput
              label="CIPC Company Reg Number"
              name="company_reg_num"
              value={formData.company_reg_num}
              onChange={handleChange}
            />
            <div className={styles.formGroup}>
              <label htmlFor="role">Role</label>
              <Dropdown
                id="role"
                name="role"
                value={formData.role}
                onChange={(value) => handleChange({ target: { name: "role", value } })}
                options={roleOptions}
                disabled={true}
                className={styles.formInput}
              />
            </div>
          </div>

          {/* Pharmacy Info Row */}
          <div className={styles.threeFields}>
            <FormInput
              label="SAPC Pharmacy Reg Number"
              name="sapc_reg_num"
              value={formData.sapc_reg_num}
              onChange={handleChange}
            />
            <FormInput
              label="Company Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
            <FormInput
              label="Company Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          {/* Personal Info Row */}
          <div className={styles.threeFields}>
            <FormInput label="First Name" name="name" value={formData.name} onChange={handleChange} />
            <FormInput
              label="Last Name"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
            />
            <FormInput
              label="ID Number"
              name="idnumber"
              value={formData.idnumber}
              onChange={handleChange}
            />
          </div>

          {/* Email & Password */}
          <div className={styles.twoFields}>
            <FormInput
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={(e) => e.target.value && checkEmailExists(e.target.value)}
            />

            <FormInput
              label="Cell Number"
              name="cellnum"
              value={formData.cellnum}
              onChange={handleChange}
            />

            {/* PasswordInput passes raw value, so adapt it to the existing handler */}
            <PasswordInput
              value={formData.password}
              onChange={(val) => handleChange(val)}
              isValid={passwordStrength.valid}
            />
          </div>

          <div className={styles.formActions}>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || isCheckingEmail}
              className={styles.submitButton1}
            >
              {isLoading ? "Submitting..." : isCheckingEmail ? "Checking..." : "Submit"}
            </Button>
          </div>

          <div className={styles.loginLink}>
            <button
              type="button"
              onClick={switchToLogin}
              className={styles.linkButton}
            >
              Already have a profile? Login here
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
